import { Component } from 'react'
import { Box, Button, KeyValueRow, Modal, Spacer, Switch, ModalTitle } from 'stylewhere/components'
import { T, __ } from 'stylewhere/i18n'
import { AppStore, Router, showToast } from 'stylewhere/shared'
import { sleep, getLanguages, showToastError } from 'stylewhere/utils'
import bcrypt from 'bcryptjs'

const ADMIN_PASSWORD_HASH = process.env.REACT_APP_ADMIN_PASSWORD_HASH ?? ''

interface PasswordValidate {
  valid: boolean
  message: string
}

interface Props {
  onClose: () => void
  visible: boolean
}

interface State {
  admin: boolean
  endpoint: string
}

export class SettingsModal extends Component<Props, State> {
  languages = getLanguages()
  state: State = { admin: false, endpoint: '' }

  componentDidMount() {
    this._init()
  }

  _init = async () => {
    const endpointUrl = await AppStore.getEndpoint()
    const adminMode = await AppStore.getAdminMode()
    this.setState({ endpoint: endpointUrl, admin: adminMode })
  }

  handleChange = (item: any) => {
    AppStore.changeLanguage(item.value)
    sleep(100).then(() => Router.refresh())
  }

  validateFields = async (pwd: string) => {
    const result: PasswordValidate = {
      valid: false,
      message: ''
    }

    if (!pwd) {
      result.message = __(T.error_messages.password_required)
    } else {
      const compare = await bcrypt.compare(pwd, ADMIN_PASSWORD_HASH)
      if (!compare) {
        result.message = __(T.error_messages.invalid_password)
      } else {
        result.valid = true
      }
    }
    return result
  }

  toggleAdminMode = async () => {
    const { admin } = this.state
    if (!admin) {
      const adminPwd = prompt('Insert admin password', '')
      const result: PasswordValidate = await this.validateFields(adminPwd)
      if (!result.valid) {
        showToastError(result.message)
        return
      }
    }
    AppStore.setAdminMode(!admin)
    this.setState({ admin: !admin })
    showToast({ status: 'success', title: admin ? 'Admin mode disabled' : 'Admin mode enabled' })
  }

  onEmulationClick = async (checked: boolean) => {
    await AppStore.enableEmulation(checked)
    this.forceUpdate()
  }

  onZbpEmulationClick = async (checked: boolean) => {
    await AppStore.enableZbpEmulation(checked)
    this.forceUpdate()
  }

  goUtilities = () => {
    Router.navigate('/utilities', {})
  }

  render() {
    const { onClose, visible } = this.props
    const { admin, endpoint } = this.state
    return (
      <Modal size="lg" onClose={onClose} visible={visible} closeOnOverlayClick>
        <Box vcenter row mb={30}>
          <ModalTitle title={__(T.titles.settings)} />
          {AppStore.isAdmin() && ADMIN_PASSWORD_HASH && (
            <Button
              size="small"
              title={admin ? 'DISABLE ADMIN MODE' : 'ADMIN MODE'}
              onClick={this.toggleAdminMode}
              variant="secondary"
            />
          )}
        </Box>
        <KeyValueRow variant="grey" label="Backend" value={endpoint} />

        <Spacer />

        <KeyValueRow
          variant="grey"
          label={__(T.misc.language)}
          value={this.languages.find((language) => language.value === AppStore.language)?.label}
          optionModal={{
            onSelect: (item) => this.handleChange(item[0]),
            field: 'label',
            options: this.languages ?? [],
            title: __(T.titles.select_language),
            selected: [{ label: this.languages.find((language) => language.value === AppStore.language)?.label }]
          }}
        />

        {AppStore.isAdmin() && admin && (
          <>
            <Spacer />
            <Button size="medium" title="Utilities" onClick={this.goUtilities} variant="secondary" />
          </>
        )}

        {AppStore.isAdmin() && admin && (
          <>
            <Spacer />
            <Switch
              onChange={this.onEmulationClick}
              check={AppStore.emulation}
              name="emulation"
              label={__(T.misc.enable_workstation_emulation)}
            />
            <Spacer />
            <Switch
              onChange={this.onZbpEmulationClick}
              check={AppStore.zbpEmulation}
              name="zbpEmulation"
              label={__(T.misc.enable_browser_print_emulation)}
            />
          </>
        )}
      </Modal>
    )
  }
}
